import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useProviderLogin } from './hooks/useProviderLogin';
import { LoginPropositions } from './components/LoginPropositions';
import { LoginProviderButtons } from './components/LoginProviderButtons';
import { LoginTermsNotice } from './components/LoginTermsNotice';
import { DevEmailLoginForm } from './components/DevEmailLoginForm';
import { LogoTextComponent } from '../../components/common/LogoTextComponent';
import { ExpauseLoadingIndicator } from '../../components/common/loadingIndicators/ExpauseLoadingIndicator';
import { InfoSheet } from '../../components/common/sheet/info/InfoSheet';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsiveDesign } from '../../responsiveDesign/responsiveDesign';
import { useLocale } from '../../contexts/LocaleContext';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { generateScreenKey, getScreenKeyPrefix } from '@/presentation/routing/screenKeyUtils';
import toast from 'react-hot-toast';
import type { AppDispatch, RootState } from '@/presentation/store';
import { setAutoLoggedOut } from '@/presentation/store/slices/auth/authSlice';
import {
  hideAccountSuspendedOverlay,
  showAccountSuspendedOverlay,
} from '@/presentation/store/slices/appOverlay/appOverlaySlice';

/**
 * LoginPage — the unauthenticated entry surface.
 *
 * Flutter source of truth: `lib/presentation/screens/login/login_screen.dart`
 * (`build()` `:235-280`, `_login` `:79-208`, `savingOverlayWidget()` `:497-512`).
 *
 * Composition — a shell over four colocated subcomponents, each painting only
 * its own internals (the page supplies the rhythm between them):
 *   - `LoginPropositions` — fading pitch + "Learn more" (Flutter `:282-335`)
 *   - `LoginProviderButtons` — Google/Apple buttons (Flutter `:337-442`)
 *   - `DevEmailLoginForm` — DEV-only `qa-tester` form (web-only, no counterpart)
 *   - `LoginTermsNotice` — terms/privacy + age-of-majority (Flutter `:444-495`)
 *
 * The single centred `responsive.auth.contentMaxWidth` column is a deliberate,
 * pre-existing desktop adaptation of Flutter's two-`Expanded` split (`:247-275`:
 * logo over the propositions in the top half, buttons + terms pinned to the
 * bottom of the lower half). Layout model is web-specific; behaviour is not.
 *
 * The page itself still owns: the `screenKey` mint + `useBasePage`; the
 * `autoLoggedOut` `InfoSheet` effect; the `useProviderLogin` outcome wiring
 * (`onRemoving` / `onError` → toast, `onSuspended` / `onSuccess` → the
 * app-level account-suspended overlay, `appOverlaySlice` → `AppOverlayHost`);
 * and the `isLoggingIn` loading overlay — Flutter's `savingOverlayWidget()`
 * (`:497-512`), a full-viewport `BackdropFilter` at sigma
 * `DimensionConstants.bottomSheetBlurSigmaX/Y` = 5
 * (`dimensions_constants.dart:109-110`) centring a bare, default-sized
 * `ExpauseLoadingIndicator`.
 *
 * The two-step sign-in flow itself (Firebase sign-in → `saveUser` →
 * removing/suspended checks → SecureStorage init/store → sync → Redux user +
 * premium packages) lives in the shared `useProviderLogin` hook — every entry
 * point (`loginWithGoogle` / `loginWithApple`, and the DEV-only
 * `loginWithEmail`) is orchestrated there, so the delete-account page can reuse
 * it unchanged.
 *
 * Flutter lifecycle ledger — `onBuildCompleted` (`:58-77`) plus `onBackPress`,
 * recorded so a later parity pass does not re-litigate each line:
 *   - `FlutterNativeSplash.remove()` (`:59`) and `SystemUtils.showStatusBar()`
 *     (`:60`) — NOT PORTED: native chrome, no browser analogue.
 *   - `setState(() => _textOpacity = 1)` (`:61-63`) — PORTED inside
 *     `LoginPropositions` (the fade is scoped to the subtree it animates).
 *   - the `autoLoggedOut` dialog (`:65-72`) — PORTED as the `InfoSheet` effect below.
 *   - `AppAuthenticationService.initializeGoogleSignIn(signOutIfSessionActive: true)`
 *     (`:74-76`) — NOT PORTED: Android-only by its own guard
 *     (`app_authentication_service.dart:122-133`, `if (Platform.isAndroid)`); the
 *     web already forces the account chooser via `prompt: 'select_account'`
 *     (`authService.ts:67-71`).
 *   - `onBackPress` → `NavigationService.popToRoot(killApp: true)` (`:46-49`) —
 *     NOT PORTED: a hardware-back app kill with no browser analogue.
 */
export const LoginPage = () => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const location = useLocation();

  // ✅ Redux state - Only for checking if authenticated (not used for rendering)
  const dispatch = useDispatch<AppDispatch>();
  const autoLoggedOut = useSelector((s: RootState) => s.auth.autoLoggedOut);

  // Screen-key mint. Deliberately NOT the canonical
  // `currentScreenKeyRef.current || generateScreenKey(...)` wiring the pages
  // under the route cache use: `/login` renders OUTSIDE `KeepAliveOutlet`
  // (`AppRoutes.tsx` declares it as a top-level `<Route>` sibling of the
  // `ProtectedRoutesWrapper` branch that renders the outlet). The outlet is the
  // only writer of the keep-alive context's `currentScreenKeyRef`
  // (`RouteStack.tsx`), so on this route that ref is either unset (fresh load)
  // or still holding the PREVIOUS protected route's key (sign-out redirect —
  // the ref lives on the always-mounted provider and the outlet unmounts
  // without clearing it). Mint our own from the shared prefix table so the key
  // is namespaced `login-…` like every other route's.
  const screenKeyRef = useRef<string>(generateScreenKey(getScreenKeyPrefix(location.pathname)));
  const screenKey = screenKeyRef.current;

  // Shared sign-in flow (Google/Apple, plus the DEV-only email entry point).
  // The page owns every UI reaction; the hook only signals outcomes.
  const { isLoggingIn, loginWithGoogle, loginWithApple, loginWithEmail } = useProviderLogin({
    onRemoving: () => toast.error(l10n.auth.removingAccountMessage),
    // Mirrors Flutter `login_screen.dart:195-201`: a suspended account raises
    // the app-level account-suspended overlay (Flutter inserts it from
    // `root_screen.dart:313-318`; the web renders it from `AppOverlayHost`).
    // That overlay is terminal by design — no Ok button and no click-outside
    // dismissal (`overlay_widget.dart:112-185`) — which a screen-scoped
    // `InfoSheet` could never reproduce.
    onSuspended: () => dispatch(showAccountSuspendedOverlay()),
    // Defensive no-op, kept deliberately. Nothing reaches it any more: the
    // overlay is suppressed only on `APP_OVERLAY_CONSTANTS.accountSuspendedExemptPaths`
    // (`/contact`, `/support`, `/legal`) and `/login` is not one of them, so it
    // covers this form for the whole suspended session and no healthy sign-in
    // can be performed from under it; on a fresh page load the flag starts
    // `false`, so the dispatch is a no-op there too. Retained because the flag
    // outlives the route on the web (Flutter's `OverlayEntry` does not) and
    // clearing a stale terminal overlay on a successful sign-in is the safe
    // default. See `appOverlaySlice.ts`'s header.
    onSuccess: () => dispatch(hideAccountSuspendedOverlay()),
    onError: (message) => toast.error(message),
  });

  // Only `onInit` / `onDispose` can run for this page: `useBasePage`'s
  // route-observer events (`onPush` / `onPushNext` / `onPopNext` / `onPop`) fire
  // only while `currentScreenKeyRef` equals the page's own `screenKey`, and
  // nothing outside `KeepAliveOutlet` ever publishes this page's self-minted key
  // there. Nothing is lost — the Flutter `onBuildCompleted` work is either
  // unportable or lives in the subcomponents (ledger in the file header above);
  // the `autoLoggedOut` sheet runs from its own effect below.
  useBasePage({ screenKey, onInit: () => { } });

  // Mirrors Flutter `login_screen.dart` `autoLoggedOut` branch: when the
  // user lands on login because UserDeviceListener signed them out due to
  // a device mismatch, surface an info sheet and clear the flag so a
  // subsequent visit to /login does not re-show it. `l10n` intentionally
  // omitted from deps — the visible trigger is `autoLoggedOut: false → true`,
  // and the closure captures the latest `l10n` from that render.
  useEffect(() => {
    if (!autoLoggedOut) return;
    dispatch(setAutoLoggedOut(false));
    InfoSheet.show({
      title: l10n.auth.autoLoggedOutTitle,
      infoText: l10n.auth.autoLoggedOutDescription,
      forceDialog: true,
      screenKey,
    });
  }, [autoLoggedOut, dispatch, screenKey]);

  return (
    // Root surface — Flutter `Scaffold(backgroundColor: AppColors.backgroundColor)` (`:245`).
    <div
      {...qaAttr({ id: 'login-form', status: isLoggingIn ? 'loading' : 'idle' })}
      className="min-h-screen flex flex-col items-center justify-center"
      style={{ backgroundColor: theme.background, padding: responsive.spacing.lg }}
    >
      {/* Centred content column — the web adaptation of Flutter's two-`Expanded`
          split (`:247-275`); see the file header. */}
      <div
        className="w-full"
        style={{
          maxWidth: responsive.auth.contentMaxWidth,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Logo */}
        <div className="flex justify-center" style={{ marginTop: responsive.gap['2xl'], marginBottom: responsive.gap['4xl'] }}>
          <LogoTextComponent />
        </div>

        {/* Propositions — the section margin is owned here; the component
            paints only its own internals. */}
        <div style={{ marginBottom: responsive.spacing['3xl'] }}>
          <LoginPropositions />
        </div>

        {/* Login Buttons */}
        <div className="flex flex-col items-center">
          <LoginProviderButtons
            isLoggingIn={isLoggingIn}
            onLoginWithGoogle={loginWithGoogle}
            onLoginWithApple={loginWithApple}
          />
          {/* DEV-ONLY QA sign-in form (excluded from production builds) */}
          {import.meta.env.DEV && (
            <DevEmailLoginForm isLoggingIn={isLoggingIn} onSubmit={loginWithEmail} />
          )}
        </div>

        {/* Terms and Privacy — the section margin is owned here; the component
            paints only its own internals. */}
        <div style={{ marginTop: responsive.spacing['3xl'] }}>
          <LoginTermsNotice />
        </div>
      </div>

      {/* Loading Overlay — Flutter `savingOverlayWidget()` (`:497-512`): a
          full-viewport `BackdropFilter` (sigma 5) centring a bare, default-sized
          `ExpauseLoadingIndicator`. `backdropFilter` + `WebkitBackdropFilter`
          mirror the sibling `BlockedOverlays` / `AppOverlayShell` pattern.
          `z-50` deliberately sits BELOW the app-level blocking overlays
          (`APP_OVERLAY_CONSTANTS.zIndex` = 10000) so an account-suspended
          overlay covers this one. */}
      {isLoggingIn && (
        <div
          {...qaAttr({ id: 'login-loading', status: 'loading' })}
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{
            backgroundColor: theme.loadingOverlay,
            backdropFilter: `blur(${responsive.auth.overlayBlur}px)`,
            WebkitBackdropFilter: `blur(${responsive.auth.overlayBlur}px)`,
          }}
        >
          <ExpauseLoadingIndicator />
        </div>
      )}
    </div>
  );
};
